
///
/// EarthBrowser Design System - Selection Coordinator
///
/// Singleton that coordinates selection state across the application.
/// Manages competing selection sources (hover info panels, focus activities)
/// with priority levels to prevent UI overlap and ensure only one
/// selection is active at each priority level.
///
/// Also owns and manages the SelectionLayer - a container node in the UIViewport
/// that holds all selection-related HUD elements (InfoPanels, FocusWindows,
/// FrameOverlays, ConnectorOverlays) for centralized z-ordering and display management.
///

import { Pane } from "@stovokor/uipanes/Pane"
import { type NodeBase } from "@stovokor/engine/NodeBase"
import { type Stovokor } from "@stovokor/Stovokor"
import { rect } from "@stovokor/math/rect"
import { type StovokorContext } from "@stovokor/StovokorContext"

/**
 * Selection priority levels. Higher priority selections hide lower priority ones.
 * HOVER: Mouse hover info panels (transient, easily dismissed)
 * FOCUS: Click-activated focus activities (persistent, requires explicit close)
 */
export enum SelectionPriority {
	HOVER = 0,
	FOCUS = 1
}

/**
 * Layer depths within the SelectionLayer.
 * Organized for proper visual stacking.
 */
export enum SelectionLayerDepth {
	/** Connector lines - behind everything */
	CONNECTOR = 1,
	/** Frame overlays - behind panels */
	FRAME = 2,
	/** Info panels (hover) - above frames */
	INFO_PANEL = 3,
	/** Focus windows - topmost */
	FOCUS_WINDOW = 4
}

/**
 * Interface for items that can be selected/coordinated.
 * Both info panels and focus activities implement this.
 */
export interface Selectable {
	/** Called by coordinator to hide this item when another takes precedence */
	hideFromCoordinator(): void
	/** Optional: unique identifier for debugging/logging */
	readonly selectionId?: string
}

/**
 * Container pane for all selection-related UI elements.
 * Provides centralized management of z-ordering and visibility.
 */
class SelectionLayer extends Pane {
	constructor(context: StovokorContext) {
		super(context)
		// High depth to render on top of other UI elements
		this.depth = 100
	}
}

/**
 * Coordinates visibility of selections across the application.
 * Ensures only one selection is active at each priority level,
 * and higher priority selections hide lower priority ones.
 * Also handles Escape key to dismiss focus selections.
 *
 * Manages the SelectionLayer which contains all HUD elements related to
 * entity selection (InfoPanels, FocusWindows, FrameOverlays, ConnectorOverlays).
 */
export class SelectionCoordinatorClass {
	private registeredItems = new Map<SelectionPriority, Set<Selectable>>([
		[SelectionPriority.HOVER, new Set()],
		[SelectionPriority.FOCUS, new Set()]
	])

	private activeSelections = new Map<SelectionPriority, Selectable | undefined>([
		[SelectionPriority.HOVER, undefined],
		[SelectionPriority.FOCUS, undefined]
	])

	private keyboardListenerInitialized = false

	/** The container layer for all selection UI elements */
	private _selectionLayer?: SelectionLayer

	/** Reference to the engine for viewport access */
	private _engine?: Stovokor

	/** Track nodes added to the selection layer */
	private managedNodes = new Set<NodeBase>()

	/** Exclusion zone for FocusWindow - InfoPanels should avoid this area */
	private _focusExclusionZone: rect = new rect()

	/** Margin around the exclusion zone */
	private readonly EXCLUSION_MARGIN = 20

	constructor(private context: StovokorContext) {

		this.initKeyboardListener()
	}

	/**
	 * Initialize the coordinator with the engine.
	 * This creates the SelectionLayer and adds it to the UIViewport.
	 * Should be called once during app initialization.
	 * @param engine - The Stovokor engine instance
	 */
	public initialize(engine: Stovokor): void {
		if (this._engine) {
			return // Already initialized
		}

		this._engine = engine
		this._selectionLayer = new SelectionLayer(this.context)
		engine.interface.addNode(this._selectionLayer)
	}

	/**
	 * Add a Pane node to the label viewport (depth-preserving pass).
	 * Labels added here render between the world and UI passes,
	 * preserving the world depth buffer for occlusion by 3D objects.
	 */
	public addLabelNode(node: Pane): void {
		if (!this._engine) {
			this.context.logger.warn("SelectionCoordinator not initialized - call initialize() first")
			return
		}

		this._engine.labels.addNode(node)
		this.managedNodes.add(node)
	}

	/**
	 * Remove a Pane node from the label viewport.
	 */
	public removeLabelNode(node: Pane): void {
		if (!this._engine) {
			return
		}

		this._engine.labels.removeNode(node)
		this.managedNodes.delete(node)
	}

	/**
	 * Get the selection layer. Creates it lazily if not initialized.
	 */
	public get selectionLayer(): SelectionLayer | undefined {
		return this._selectionLayer
	}

	/**
	 * Add a Pane node to the selection layer.
	 * Use this instead of adding nodes to activity.uiNodes for selection-related Pane elements.
	 * @param node - The Pane to add (InfoPanel, FocusWindow, FrameOverlay, etc.)
	 * @param depth - Optional depth override for z-ordering
	 */
	public addNode(node: Pane, depth?: SelectionLayerDepth): void {
		if (!this._selectionLayer) {
			this.context.logger.warn("SelectionCoordinator not initialized - call initialize() first")
			return
		}

		if (depth !== undefined) {
			node.depth = depth
		}

		this._selectionLayer.addNode(node)
		this.managedNodes.add(node)
	}

	/**
	 * Remove a Pane node from the selection layer.
	 * @param node - The Pane to remove
	 */
	public removeNode(node: Pane): void {
		if (!this._selectionLayer) {
			return
		}

		this._selectionLayer.removeNode(node)
		this.managedNodes.delete(node)
	}

	/**
	 * Add multiple Pane nodes to the selection layer at once.
	 * @param nodes - Array of Panes to add
	 */
	public addNodes(nodes: Pane[]): void {
		nodes.forEach(node => this.addNode(node))
	}

	/**
	 * Remove multiple Pane nodes from the selection layer.
	 * @param nodes - Array of Panes to remove
	 */
	public removeNodes(nodes: Pane[]): void {
		nodes.forEach(node => this.removeNode(node))
	}

	/**
	 * Initialize global keyboard listener for Escape key handling.
	 * Automatically called on construction.
	 */
	private initKeyboardListener(): void {
		if (this.keyboardListenerInitialized) return
		if (typeof window === "undefined") return // SSR safety

		window.addEventListener("keydown", this.handleKeyDown.bind(this))
		this.keyboardListenerInitialized = true
	}

	/**
	 * Handle keyboard events. Escape key dismisses the active focus selection.
	 */
	private handleKeyDown(event: KeyboardEvent): void {
		if (event.key === "Escape") {
			const activeFocus = this.activeSelections.get(SelectionPriority.FOCUS)
			if (activeFocus) {
				activeFocus.hideFromCoordinator()
				this.activeSelections.set(SelectionPriority.FOCUS, undefined)
				event.preventDefault()
			}
		}
	}

	/**
	 * Register a selectable item with the coordinator.
	 * @param item - The selectable item to register
	 * @param priority - The priority level for this item
	 */
	public register(item: Selectable, priority: SelectionPriority): void {
		this.registeredItems.get(priority)?.add(item)
	}

	/**
	 * Unregister a selectable item from the coordinator.
	 * @param item - The selectable item to unregister
	 * @param priority - The priority level of the item
	 */
	public unregister(item: Selectable, priority: SelectionPriority): void {
		this.registeredItems.get(priority)?.delete(item)
		if (this.activeSelections.get(priority) === item) {
			this.activeSelections.set(priority, undefined)
		}
	}

	/**
	 * Notify the coordinator that an item wants to become active.
	 * - Hides any currently active item at the same priority level
	 * - For FOCUS priority, also hides any active HOVER items
	 * @param item - The item that wants to show
	 * @param priority - The priority level of the item
	 * @returns true if the item should proceed to show
	 */
	public willShow(item: Selectable, priority: SelectionPriority): boolean {
		// Hide any active selection at the same priority level
		const currentActive = this.activeSelections.get(priority)
		if (currentActive && currentActive !== item) {
			currentActive.hideFromCoordinator()
		}

		// If this is a FOCUS selection, also hide any active HOVER selection
		if (priority === SelectionPriority.FOCUS) {
			const hoverActive = this.activeSelections.get(SelectionPriority.HOVER)
			if (hoverActive) {
				hoverActive.hideFromCoordinator()
				this.activeSelections.set(SelectionPriority.HOVER, undefined)
			}
		}

		this.activeSelections.set(priority, item)
		return true
	}

	/**
	 * Notify the coordinator that an item is hiding.
	 * @param item - The item that is hiding
	 * @param priority - The priority level of the item
	 */
	public willHide(item: Selectable, priority: SelectionPriority): void {
		if (this.activeSelections.get(priority) === item) {
			this.activeSelections.set(priority, undefined)
		}
	}

	/**
	 * Check if a focus selection is currently active.
	 * @returns true if a focus selection is active
	 */
	public isFocusActive(): boolean {
		return this.activeSelections.get(SelectionPriority.FOCUS) !== undefined
	}

	/**
	 * Set the exclusion zone for the FocusWindow.
	 * InfoPanels will avoid overlapping this area.
	 * Should be called by FocusActivities when updating their window position.
	 * @param x - Left edge of the exclusion zone
	 * @param y - Top edge of the exclusion zone
	 * @param width - Width of the exclusion zone
	 * @param height - Height of the exclusion zone
	 */
	public setFocusExclusionZone(x: number, y: number, width: number, height: number): void {
		// Add margin around the zone
		this._focusExclusionZone.left = x - this.EXCLUSION_MARGIN
		this._focusExclusionZone.top = y - this.EXCLUSION_MARGIN
		this._focusExclusionZone.width = width + this.EXCLUSION_MARGIN * 2
		this._focusExclusionZone.height = height + this.EXCLUSION_MARGIN * 2
	}

	/**
	 * Clear the exclusion zone when the FocusWindow closes.
	 */
	public clearFocusExclusionZone(): void {
		this._focusExclusionZone.left = 0
		this._focusExclusionZone.top = 0
		this._focusExclusionZone.width = 0
		this._focusExclusionZone.height = 0
	}

	/**
	 * Get the current focus exclusion zone.
	 * @returns The exclusion zone rect (with margin applied)
	 */
	public get focusExclusionZone(): rect {
		return this._focusExclusionZone
	}

	/**
	 * Check if a rect overlaps with the focus exclusion zone.
	 * @param x - Left edge of the rect to check
	 * @param y - Top edge of the rect to check
	 * @param width - Width of the rect
	 * @param height - Height of the rect
	 * @returns true if there is overlap
	 */
	public overlapsExclusionZone(x: number, y: number, width: number, height: number): boolean {
		if (!this.isFocusActive() || this._focusExclusionZone.width === 0) {
			return false
		}

		const zone = this._focusExclusionZone
		return !(x + width < zone.left ||
				 x > zone.left + zone.width ||
				 y + height < zone.top ||
				 y > zone.top + zone.height)
	}

	/**
	 * Get the currently active selection at a given priority level.
	 * @param priority - The priority level to check
	 */
	public getActiveSelection(priority: SelectionPriority): Selectable | undefined {
		return this.activeSelections.get(priority)
	}

	/**
	 * Hide all selections at a given priority level.
	 * @param priority - The priority level to clear
	 */
	public hideAll(priority: SelectionPriority): void {
		const active = this.activeSelections.get(priority)
		if (active) {
			active.hideFromCoordinator()
			this.activeSelections.set(priority, undefined)
		}
	}

	/**
	 * Hide all selections at all priority levels.
	 */
	public hideAllSelections(): void {
		for (const priority of this.activeSelections.keys()) {
			this.hideAll(priority)
		}
	}

	/**
	 * Clean up when the coordinator is no longer needed.
	 */
	public destroy(): void {
		if (this._selectionLayer && this._engine) {
			this._engine.interface.removeNode(this._selectionLayer)
		}
		this.managedNodes.clear()
		this._selectionLayer = undefined
		this._engine = undefined
	}
}
